import { useRef } from 'react';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';

export type RefreshWhenDone = {
  // Record property watched after the update (e.g. 'status')
  property: string;
  // Values meaning the background processing is still running (e.g. ['pending'])
  whileIn: string[];
  // Notification shown once every record is done
  doneMessage?: string;
  interval?: number;
  timeout?: number;
};

const DEFAULT_INTERVAL = 3000;
const DEFAULT_TIMEOUT = 60000;

// Polls the given records after an update that triggers a background job, and
// reloads the page once none of them is in a "still running" state anymore.
// Gives up silently after the timeout (the user can still reload manually).
// The polling deliberately survives the button unmounting: bulk action buttons
// disappear as soon as the selection is cleared after the update.
const useRefreshWhenDone = (
  resource: string,
  options: RefreshWhenDone | undefined
): ((ids: string[]) => void) => {
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();
  const timer = useRef<ReturnType<typeof setTimeout>>();

  return (ids: string[]) => {
    if (!options || !ids.length) return;

    const {
      property,
      whileIn,
      doneMessage,
      interval = DEFAULT_INTERVAL,
      timeout = DEFAULT_TIMEOUT,
    } = options;
    const deadline = Date.now() + timeout;
    if (timer.current) clearTimeout(timer.current);

    const poll = () => {
      timer.current = setTimeout(async () => {
        try {
          const { data } = await dataProvider.getMany(resource, { ids });
          const running = data.some((record: Record<string, unknown>) =>
            whileIn.includes(String(record[property]))
          );
          if (!running) {
            refresh();
            if (doneMessage) notify(doneMessage, 'success');

            return;
          }
        } catch (e) {
          // Network hiccup: keep polling until the deadline
        }
        if (Date.now() < deadline) poll();
      }, interval);
    };

    poll();
  };
};

export default useRefreshWhenDone;
